import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  { icon: Shield, title: "Technical SEO audits" },
  { icon: Layers, title: "On-page SEO improvements" },
  { icon: TrendingUp, title: "Keyword research" },
  { icon: Code, title: "Content optimization" },
  { icon: Smartphone, title: "Local SEO services in Vancouver" },
  { icon: Zap, title: "Speed and performance improvements" },
  { icon: TrendingUp, title: "Monthly tracking and reporting" },
];


const processSteps = [
  { number: 1, title: "Full SEO audit" },
  { number: 2, title: "Clear keyword plan" },
  { number: 3, title: "Website optimization" },
  { number: 4, title: "Technical fixes" },
  { number: 5, title: "Local SEO setup" },
  { number: 6, title: "Monthly reports" },
  { number: 7, title: "Ongoing improvements" },
];
const faqs = [
  {
    question: "How long does SEO take?",
    answer:
      "SEO takes time. Most businesses see steady improvement over several months.",
  },
  {
    question: "Do you guarantee rankings?",
    answer:
      "No agency can guarantee rankings. We focus on strong, long-term strategies.",
  },
  {
    question: "Can you improve my current website?",
    answer:
      "Yes. We can work on existing websites or recommend improvements.",
  },
  {
    question: "Do you offer small business SEO services?",
    answer:
      "Yes. We work with small and growing businesses.",
  },
  {
    question: "Do you provide ongoing SEO support?",
    answer:
      "Yes. SEO requires continuous improvement and monitoring.",
  },
];


const whyChooseUs = [
  {
    title: "Technical SEO Improvements",
    description:
      "We fix crawl errors, indexing problems, and site performance issues.",
  },
  {
    title: "Keyword Research & Targeting",
    description:
      "We identify keywords your customers are searching for.",
  },
  {
    title: "SEO + Website Knowledge",
    description:
      "Because we build websites, we understand how structure affects rankings.",
  },
];


export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      {/* SEO Meta */}
<SEO
  title="SEO Services Vancouver | SEO Agency | Square Trigger"
  description="Square Trigger provides SEO services in Vancouver including technical SEO, local SEO, and keyword optimization to improve your website rankings."
  ogTitle="SEO Services Vancouver | SEO Agency | Square Trigger"
  ogDescription="Square Trigger provides SEO services in Vancouver including technical SEO, local SEO, and keyword optimization to improve your website rankings."
/>



    {/* Hero Section */}
<section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
  <div className="absolute inset-0">
    <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
    <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <div className="max-w-4xl mx-auto text-center">
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
      >
        SEO Services in{" "}
        <span className="gradient-text">Vancouver</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
      >
        Square Trigger helps businesses improve their Google rankings, increase website
        traffic, and get more qualified leads through practical SEO strategies.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
      >
        {/* Primary CTA */}
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Get a Free SEO Consultation
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>

        {/* Secondary CTA */}
        <Button
          variant="glass"
          size="lg"
          asChild
          className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
        >
          <Link to="/seo-audit">Request an SEO Audit</Link>
        </Button>
      </motion.div>
    </div>
  </div>
</section>


    {/* Value Statement */}
<section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-5xl mx-auto text-center"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
        Professional SEO Services{" "}
        <span className="gradient-text">That Deliver Real Results</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-4">
        At <strong>Square Trigger</strong>, we provide SEO services in Vancouver
        focused on improving search visibility and bringing consistent traffic
        to your website.
      </p>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        We improve website structure, fix technical issues, optimize content,
        and build a clear keyword strategy so your business can rank higher in
        search results. Our goal is simple — help your website get found by the
        right people.
      </p>
    </motion.div>
  </div>
</section>


     {/* Why Choose Us */}
<section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">

    {/* Section Heading */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12"
    >
      <p className="text-sm font-semibold tracking-wide text-primary uppercase mb-2">
        Why Choose Us
      </p>

      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        A Practical SEO Agency in Vancouver
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        We focus on clear strategies and measurable improvements
      </p>
    </motion.div>

    {/* Main Content Grid */}
    <div className="grid lg:grid-cols-2 gap-10 items-start">

      {/* Right Side Points */}
      <div className="space-y-6">
  {whyChooseUs.map((item, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
    >
      <h3 className="font-semibold text-foreground mb-2">
        {item.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {item.description}
      </p>
    </motion.div>
  ))}
</div>

      {/* Trust Block */}
      <div className="space-y-6">
  <div className="p-6 bg-card rounded-2xl border border-border shadow-soft text-center sm:text-left flex flex-col items-center sm:items-start">
    <h3 className="font-semibold text-foreground mb-2">
      100% Clear Strategy
    </h3>
    <p className="text-sm text-muted-foreground leading-relaxed">
      Every SEO campaign starts with proper research and planning.
    </p>
  </div>

  <div className="p-6 bg-card rounded-2xl border border-border shadow-soft text-center sm:text-left flex flex-col items-center sm:items-start">
    <h3 className="font-semibold text-foreground mb-2">
      Transparent Reporting
    </h3>
    <p className="text-sm text-muted-foreground leading-relaxed">
      You receive clear updates and measurable results.
    </p>
  </div>
</div>

    </div>

  </div>
</section>


     {/* SEO Services */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">

    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
    >
      {/* Eyebrow */}
      <p className="text-sm font-semibold tracking-wide text-primary uppercase mb-2">
        What We Offer
      </p>

      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Our SEO Services
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        We provide complete search engine optimization services in Vancouver, including:
      </p>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {capabilities.map((capability, index) => {
        const Icon = capability.icon;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            className="h-full"
          >
           <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
  <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
    <Icon className="h-6 w-6 text-primary-foreground" />
  </div>

  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
    {capability.title}
  </h3>
</Card>
          </motion.div>
        );
      })}
    </div>

  </div>
</section>

{/* What You Receive */}
<section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">

    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        What You <span className="gradient-text">Receive</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        When you work with Square Trigger, you get:
      </p>
    </motion.div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {processSteps.map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.08 }}
          className="relative h-full"
        >
         <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
  <div className="mb-4 w-full flex justify-center sm:justify-start">
    <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center">
      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
    </div>
  </div>

  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
    {step.title}
  </h3>
</Card>
        </motion.div>
      ))}
    </div>

  </div>
</section>

{/* How Our SEO Process Works */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 left-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        How Our <span className="gradient-text">SEO Process Works</span>
      </h2>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {[
        {
          number: "01",
          title: "Website Audit",
          description:
            "We review your website for technical and structural issues.",
        },
        {
          number: "02",
          title: "Keyword Research",
          description:
            "We find search terms that match your business goals.",
        },
        {
          number: "03",
          title: "On-Page Optimization",
          description:
            "We improve titles, content, structure, and internal linking.",
        },
        {
          number: "04",
          title: "Technical Fixes",
          description:
            "We resolve indexing and performance problems.",
        },
        {
          number: "05",
          title: "Ongoing Monitoring",
          description:
            "We track rankings and adjust the strategy when needed.",
        },
      ].map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.07 }}
          className="h-full"
        >
          <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
  <div className="mb-4 w-full flex justify-center sm:justify-start">
    <div className="text-3xl font-display font-bold gradient-text">
      {step.number}
    </div>
  </div>

  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
    {step.title}
  </h3>

  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
    {step.description}
  </p>
</Card>
        </motion.div>
      ))}
    </div>
  </div>
</section>

{/* Local SEO Services in Vancouver */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-12 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">

    {/* Section Heading */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
        Local SEO Services in{" "}
        <span className="gradient-text">Vancouver</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4">
        If your business serves a local area, we help you rank in local searches.
      </p>

      <p className="text-base sm:text-lg font-semibold text-foreground">
        We optimize:
      </p>
    </motion.div>

    {/* Points Grid */}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto mb-10">
      {[
        "Google Business Profile",
        "Local keywords",
        "Location-based pages",
        "Local citations",
        "Map listings",
      ].map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.07 }}
        >
         <Card className="p-6 border-border hover:border-primary/30 transition-all duration-300 card-hover">
  <div className="flex flex-col sm:flex-row items-center sm:items-center justify-center sm:justify-start gap-3 text-center sm:text-left">
    <div className="w-3 h-3 rounded-full bg-primary flex-shrink-0" />
    <h3 className="text-base sm:text-lg font-semibold text-foreground">
      {item}
    </h3>
  </div>
</Card>
        </motion.div>
      ))}
    </div>

    {/* Closing Line */}
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto"
    >
      Our local SEO services in Vancouver help customers find you nearby.
    </motion.p>

  </div>
</section>
      

      {/* FAQs */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
  SEO Services – <span className="gradient-text">FAQs</span>
</h2>

    </motion.div>

    <div className="max-w-3xl mx-auto space-y-4">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.05 }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <button
            onClick={() => setOpenFaq(openFaq === index ? null : index)}
            className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors"
          >
            <span className="font-semibold text-foreground pr-6">
              {faq.question}
            </span>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                openFaq === index ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {openFaq === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>

    {/* FAQ Schema */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      })}
    </script>
  </div>
</section>

     {/* Final CTA */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
        Improve Your{" "}
        <span className="gradient-text">Search Rankings</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
        Let’s create a clear SEO plan for your business.
      </p>

      <div className="flex justify-center">
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Start Your SEO Strategy
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
    </motion.div>
  </div>
</section>

    </Layout>
  );
}